import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Tabs } from '../ui';
import useUnitDetail from '../../hooks/useUnitDetail';
import useUnitActivities from '../../hooks/useUnitActivities';
import { UNIT_TABS, unitShareUrl, unitTabLabel } from '../../utils/unitRoute';
import { activeQuotesFor } from '../../utils/unitIntelligence';
import UnitHero from './UnitHero';
import Lightbox from './Lightbox';
import TAB_PANELS from './tabs';

/** The Ask IronSight tab label: a dot that says whether the answer is ready. */
function AskLabel({ state }) {
    const generating = state === 'generating' || state === 'idle';
    const srState = {
        generating: 'generating', idle: 'generating', ready: 'ready', error: 'failed', off: 'not available',
    }[state];
    return (
        <>
            {state !== 'off' && (
                <span className={`u2-ai-dot${generating ? ' is-live' : ''}${state === 'error' ? ' is-error' : ''}`} aria-hidden="true" />
            )}
            Ask IronSight
            <span className="v2-sr"> ({srState})</span>
        </>
    );
}
AskLabel.propTypes = { state: PropTypes.string.isRequired };

/**
 * One unit on the full page (U1): hero (U4), sticky tabs with counts and the
 * nine tabs. Mounted per stock number (the route keys it), so every piece of
 * per-unit state starts fresh on prev / next (P-UNIT-016): the loads, the
 * calculator, the Service brief and chat, the comps filters, the lightbox.
 * Service History stays mounted across tab switches (P-UNIT-014 / 078).
 *
 * `committed`: the unit came from the committed units (sale pending,
 * pre-sold, sold). Ask IronSight is then never generated (its advice is about
 * selling the unit) and the tabs show "Not available for committed units"
 * where a card only makes sense for a unit still for sale (ctx.committed).
 */
export default function UnitPageV2({
    unit, committed = false, tab, onTabChange, createOnOpen, allUnits, machineCatalog, readOnly, showToast, titleRef,
}) {
    const detail = useUnitDetail(unit, tab, { eagerImages: true, tractorHouse: true, agent: !committed });
    const activities = useUnitActivities(unit.StockNumber);
    const [lightboxIndex, setLightboxIndex] = useState(null);
    // "Create task" (hero, or arriving from the inventory's Create task) opens
    // the Activity tab's create form; each request bumps the nonce. The
    // Activity tab consumes a request once it opened the form, so a later
    // visit to the tab (it remounts) does not open it again (R2-B4).
    const [createReq, setCreateReq] = useState(() => {
        const asked = Boolean(createOnOpen && !readOnly);
        return { nonce: asked ? 1 : 0, pending: asked };
    });
    const createNonce = createReq.pending ? createReq.nonce : 0;
    const consumeCreate = useCallback(() => {
        setCreateReq((r) => (r.pending ? { ...r, pending: false } : r));
    }, []);
    const pendingAnchor = useRef(null);

    const goTab = useCallback((id, { anchor } = {}) => {
        pendingAnchor.current = anchor || null;
        onTabChange(id);
    }, [onTabChange]);

    // After a jump to a tab with an anchor (e.g. "Open carrying cost and
    // margin calculator"), bring that element into view.
    useEffect(() => {
        const id = pendingAnchor.current;
        if (!id) return;
        pendingAnchor.current = null;
        const el = document.getElementById(id);
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
    }, [tab]);

    const openLightbox = useCallback((i) => setLightboxIndex(i), []);
    const openHeroPhoto = (i) => {
        onTabChange('photos');
        setLightboxIndex(i);
    };

    const createTask = () => {
        setCreateReq((r) => ({ nonce: r.nonce + 1, pending: true }));
        onTabChange('activity');
    };

    const copyLink = () => {
        const url = unitShareUrl(unit.StockNumber, tab);
        const done = () => showToast({ text: `Link copied. It opens #${unit.StockNumber} on ${unitTabLabel(tab)}` });
        const failed = () => showToast({ text: `Could not copy the link: ${url}` });
        try {
            const p = navigator.clipboard?.writeText(url);
            if (p && typeof p.then === 'function') p.then(done, failed);
            else if (p === undefined) failed();
            else done();
        } catch (e) {
            failed();
        }
    };

    const ctx = {
        unit,
        committed,
        detail,
        activities,
        allUnits,
        machineCatalog,
        readOnly,
        goTab,
        openLightbox,
        showToast,
        createNonce,
        onCreateConsumed: consumeCreate,
    };

    // The live-quote figure from the pipeline's open quotes once both loads
    // are in (null when there is none); undefined until then (QA BUG-4).
    const liveQuote = detail.pipeline && detail.quoteEquipment
        ? activeQuotesFor(detail.pipeline, detail.quoteEquipment)
            .reduce((max, q) => (q.selling_price > (max ?? 0) ? q.selling_price : max), null)
        : undefined;

    const photoCount = detail.imagesFetched ? detail.images.length : null;
    const openDeals = detail.pipeline?.open?.length || 0;
    const counts = {
        photos: photoCount || null,
        pipeline: openDeals || null,
        activity: activities.openCount || null,
    };

    const tabs = UNIT_TABS.map((t) => {
        const Panel = TAB_PANELS[t.id];
        return {
            id: t.id,
            label: t.id === 'fasttrack' ? <AskLabel state={committed ? 'off' : detail.agentState} /> : t.label,
            count: counts[t.id],
            // Service History keeps its brief, chat and selection across tab
            // switches; the other tabs mount when shown (P-UNIT-014).
            keepMounted: t.id === 'service',
            panel: t.id === tab || t.id === 'service' ? <Panel ctx={ctx} /> : undefined,
        };
    });

    return (
        <>
            <UnitHero
                unit={unit}
                committed={committed}
                machineCatalog={machineCatalog}
                images={detail.images}
                imagesLoading={detail.imagesLoading}
                openTasks={activities.openCount}
                liveQuote={liveQuote}
                listingUrl={detail.thDetail?.listing_url || null}
                readOnly={readOnly}
                onGoTab={goTab}
                onOpenPhoto={openHeroPhoto}
                onCreateTask={createTask}
                onCopyLink={copyLink}
                titleRef={titleRef}
            />
            <Tabs
                label="Unit detail sections"
                className="u2-tabs"
                panelClassName="u2-panel"
                value={tab}
                onChange={onTabChange}
                tabs={tabs}
            />
            <Lightbox
                images={detail.images}
                index={lightboxIndex}
                onIndexChange={setLightboxIndex}
                onClose={() => setLightboxIndex(null)}
                unit={unit}
            />
        </>
    );
}

UnitPageV2.propTypes = {
    unit: PropTypes.object.isRequired,
    committed: PropTypes.bool,
    tab: PropTypes.string.isRequired,
    onTabChange: PropTypes.func.isRequired,
    createOnOpen: PropTypes.bool,
    allUnits: PropTypes.array,
    machineCatalog: PropTypes.object,
    readOnly: PropTypes.bool,
    showToast: PropTypes.func.isRequired,
    titleRef: PropTypes.object,
};
